// Knopf „Custom Activity ▾“ oben rechts auf der Lead-Seite: öffnet ein kleines Menü mit allen Custom-Activity-Typen
// (Reihenfolge 0–4 wie in der Typdefinition). Pfeiltasten + Enter, Ziffer wählt direkt, Esc schließt.
// Ein Punkt am Eintrag zeigt einen angefangenen Entwurf für diesen Lead.
import { useEffect, useMemo, useRef, useState } from "react";
import type { CustomActivityType } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { hatEntwurf } from "../../daten/entwuerfe";
import { useMeta } from "../../daten/meta";
import { darfAendern } from "../../werkzeuge/felder";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import "../../styles/lead.css";

/** „1 | Entscheider erreicht“ -> { nr: "1", text: "Entscheider erreicht" } */
export function typTeile(name: string): { nr: string | null; text: string } {
  const m = /^\s*(\d+)\s*[|·:.-]\s*(.+)$/.exec(name);
  return m ? { nr: m[1], text: m[2].trim() } : { nr: null, text: name };
}

/** Typen, die der angemeldete Nutzer hier eintragen darf, sortiert nach Name (0 | …, 1 | …). */
export function useCaTypen(): CustomActivityType[] {
  const meta = useMeta();
  const { nutzer } = useAuth();
  return useMemo(
    () =>
      (meta.meta?.custom_activity_types ?? [])
        .filter((t) => !t.is_archived && !t.api_create_only && darfAendern(t, nutzer?.role ?? "user"))
        .sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true })),
    [meta.meta, nutzer?.role],
  );
}

/** Typname mit Nummernmarke, wie im Menü und im Feed */
export function TypName({ name }: { name: string }) {
  const { nr, text } = typTeile(name);
  return (
    <>
      {nr !== null ? <span className="ls-typnr">{nr}</span> : null}
      <span className="ls-typtext">{text}</span>
    </>
  );
}

interface Props {
  leadId: string;
  offen: boolean;
  setOffen: (o: boolean) => void;
  onWahl: (typId: string) => void;
  /** gerade offenes Formular (Knopf hervorheben) */
  aktiv?: string | null;
}

export function CustomActivityKnopf({ leadId, offen, setOffen, onWahl, aktiv }: Props) {
  const typen = useCaTypen();
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const istAktiv = !!aktiv && typen.some((t) => t.id === aktiv);
  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={`knopf knopf--primaer ls-ca-knopf${istAktiv ? " ls-ca-knopf--an" : ""}`}
        aria-haspopup="menu"
        aria-expanded={offen}
        title="Custom Activity eintragen (a)"
        disabled={!typen.length}
        onClick={(e) => {
          e.stopPropagation();
          setOffen(!offen);
        }}
      >
        <Icon name="aktivitaet" groesse={14} />
        Custom Activity
        <Icon name="chevron-unten" groesse={13} />
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={300} rolle="menu">
        <TypListe
          leadId={leadId}
          typen={typen}
          aktiv={aktiv}
          onWahl={(id) => {
            setOffen(false);
            onWahl(id);
          }}
        />
      </Ausklapp>
    </>
  );
}

function TypListe({ leadId, typen, aktiv, onWahl }: { leadId: string; typen: CustomActivityType[]; aktiv?: string | null; onWahl: (id: string) => void }) {
  const [pos, setPos] = useState(() => Math.max(0, typen.findIndex((t) => t.id === aktiv)));
  const liste = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = liste.current;
    el?.focus({ preventScroll: true });
    const t = window.setTimeout(() => {
      if (el && el.isConnected && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div
      ref={liste}
      className="menue ls-ca-menue"
      tabIndex={-1}
      aria-label="Custom Activity wählen"
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setPos((p) => (e.key === "ArrowDown" ? (p + 1) % typen.length : (p - 1 + typen.length) % typen.length));
        } else if (e.key === "Enter" && typen[pos]) {
          e.preventDefault();
          onWahl(typen[pos].id);
        } else if (/^\d$/.test(e.key)) {
          const t = typen.find((x) => typTeile(x.name).nr === e.key);
          if (t) {
            e.preventDefault();
            onWahl(t.id);
          }
        }
      }}
    >
      <div className="menue__ueberschrift">Custom Activity eintragen</div>
      {typen.map((t, i) => {
        const entwurf = hatEntwurf(`${leadId}|${t.id}`);
        return (
          <button
            key={t.id}
            type="button"
            role="menuitem"
            className={`menue__punkt ls-ca-menue__punkt${i === pos ? " menue__punkt--aktiv" : ""}`}
            title={t.description ?? t.name}
            onMouseEnter={() => setPos(i)}
            onClick={() => onWahl(t.id)}
          >
            <TypName name={t.name} />
            {entwurf ? <span className="ls-ca-menue__entwurf">Entwurf</span> : null}
          </button>
        );
      })}
      {typen.length ? <div className="ls-ca-menue__fuss">Ziffer wählt direkt · Esc schließt</div> : null}
    </div>
  );
}
